import React, { useState, useMemo } from 'react';
import { 
    Card, 
    Box, 
    Typography, 
    Collapse, 
    IconButton, 
    Chip, 
    Stack, 
    Divider 
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getModeVisual } from '../Helpers/modeVisuals';
import { Storage, RecentService } from '../Types/Storage';
import HealthWidgetBase from './StorageHealthWidget/HealthWidgetBase';
import RecentServicesWidget from './RecentServicesWidget'; 

interface MobileStorageCardProps {
    storage: Storage;
    healthIndex?: number | null;
    currentMode?: string | null;
    recentServices: RecentService[];
    servicesLoading: boolean;
}

const toTitleCase = (value: string | null | undefined) => {
    if (!value) return 'Unknown';
    return value
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const MobileStorageCard: React.FC<MobileStorageCardProps> = ({ 
    storage, 
    healthIndex, 
    currentMode, 
    recentServices,
    servicesLoading 
}) => {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(false);

    const modeLabel = toTitleCase(currentMode);
    const { chipColor, accentColor } = useMemo(() => getModeVisual(currentMode ?? null), [currentMode]);

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };
    
    // Determine health index color consistent with HealthIndexOverview
    const healthColor = useMemo(() => {
        if (healthIndex === null || healthIndex === undefined) return 'text.secondary';
        if (healthIndex >= 80) return 'success.main';
        if (healthIndex >= 60) return 'warning.main';
        if (healthIndex >= 40) return 'warning.dark';
        return 'error.main';
    }, [healthIndex]);

    // Visual calculations matches IoTClientSummary
    const cardVisuals = useMemo(() => {
        const paletteKey = chipColor && chipColor !== 'default' ? chipColor : null;
        const paletteColor = paletteKey ? (theme.palette as any)[paletteKey] : null;
        const baseColor = accentColor || paletteColor?.main || null;

        if (baseColor) {
            return {
                backgroundColor: alpha(baseColor, 0.08), // Slightly lighter for the card background
                borderColor: alpha(baseColor, 0.5),
                chipVariant: 'filled' as const
            };
        }

        return {
            backgroundColor: theme.palette.background.paper,
            borderColor: theme.palette.divider,
            chipVariant: 'outlined' as const
        };
    }, [accentColor, chipColor, theme]);

    return (
        <Card 
            variant="outlined" 
            sx={{ 
                borderColor: cardVisuals.borderColor,
                backgroundColor: cardVisuals.backgroundColor,
                transition: 'all 0.3s ease',
            }}
        >
            <Box 
                onClick={handleExpandClick}
                sx={{ 
                    p: 2, 
                    cursor: 'pointer',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    gap: 1
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1} overflow="hidden">
                     <Typography variant="subtitle1" fontWeight="bold" noWrap>
                        {storage.name}
                    </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1} flexShrink={0}>
                    {/* Health Index Display */ }
                    {healthIndex !== null && healthIndex !== undefined && (
                        <Typography 
                            variant="body2" 
                            fontWeight="bold"
                            color={healthColor}
                        >
                            {Math.round(healthIndex)}%
                        </Typography>
                    )}
                    
                    {/* Mode Chip */}
                    <Chip 
                        label={modeLabel} 
                        color={chipColor} 
                        variant={chipColor === 'default' ? 'outlined' : 'filled'}
                        size="small"
                        sx={{ fontWeight: 500 }}
                    />

                    <IconButton
                        size="small"
                        sx={{
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: theme.transitions.create('transform', {
                                duration: theme.transitions.duration.shortest,
                            }),
                        }}
                    >
                        <ExpandMoreIcon />
                    </IconButton>
                </Stack>
            </Box>

            <Collapse in={expanded} timeout="auto">
                <Divider sx={{ borderColor: alpha(cardVisuals.borderColor, 0.3) }} />
                <Box p={2} display="flex" flexDirection="column" gap={3}>
                    {/* Render existing widgets */}
                    <Box>
                        <HealthWidgetBase storage={storage} />
                    </Box>
                    <Box>
                        <RecentServicesWidget 
                            services={recentServices} 
                            loading={servicesLoading} 
                        />
                    </Box>
                </Box>
            </Collapse>
        </Card>
    );
};

export default MobileStorageCard;
